import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import OrgSwitcher from "./OrgSwitcher";
import {
  House,
  MagnifyingGlass,
  Pulse,
  Gear,
  Users,
  SignOut,
  List,
  X,
  ChartBar,
  Buildings,
  UploadSimple,
  LinkSimple
} from "@phosphor-icons/react";

const LOGO_URL = "/dolphin-retail-logo.webp";

const NavItem = ({ to, icon: Icon, label, active, onClick, compact = false }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center gap-3 ${compact ? "px-3 py-2.5" : "px-4 py-3"} rounded-xl transition-colors duration-150 ${
      active
        ? "nav-active"
        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
    }`}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <Icon
      size={20}
      weight={active ? "fill" : "regular"}
      className={`flex-shrink-0 ${active ? "" : "text-slate-400"}`}
    />
    <span className="font-medium text-sm">{label}</span>
  </Link>
);

const BottomBlock = ({ user, onLogout, compact = false }) => (
  <div className="flex-shrink-0 border-t border-slate-200">
    <div className="p-3">
      <OrgSwitcher />
      {user?.organization_name && (
        <div className="px-3 pt-2 pb-1">
          <p className="text-[10px] uppercase tracking-wider text-slate-400">Home Organization</p>
          <p className="text-xs font-medium text-[#006994] truncate" data-testid="org-name-display">{user.organization_name}</p>
        </div>
      )}
      <div className="flex items-center gap-3 px-3 py-2">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#006994] to-[#0EA5E9] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {user?.name?.charAt(0) || "U"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate text-sm">{user?.name}</p>
          <p className="text-xs text-slate-500 truncate capitalize">{user?.role?.replace("_", " ")}</p>
        </div>
      </div>
      <button
        onClick={onLogout}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-slate-500 hover:text-red-400 hover:bg-red-50 rounded-md transition-colors duration-200"
        data-testid={compact ? "mobile-logout-btn" : "logout-btn"}
      >
        <SignOut size={18} />
        <span className="font-medium text-sm">Logout</span>
      </button>
    </div>
    <p className="px-4 pb-2 text-[10px] text-slate-400 text-center">Dolphin Digital OOH Media &copy; 2026</p>
  </div>
);

const DashboardLayout = ({ children }) => {
  const { user, logout, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto-close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/dashboard", icon: House, label: "Dashboard" },
    { to: "/jobs/new", icon: MagnifyingGlass, label: "New Job" },
    { to: "/jobs/upload", icon: UploadSimple, label: "Upload List" },
    { to: "/jobs/firecrawl", icon: LinkSimple, label: "Fire Crawl" },
    { to: "/settings", icon: Gear, label: "Settings" },
  ];

  if (user?.role === "admin" || user?.role === "super_admin") {
    navItems.push({ to: "/users", icon: Users, label: "Users" });
    navItems.push({ to: "/analytics", icon: ChartBar, label: "Analytics" });
  }

  if (isSuperAdmin) {
    navItems.push({ to: "/organizations", icon: Buildings, label: "Organizations" });
    navItems.push({ to: "/activity", icon: Pulse, label: "Activity" });
  }

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  // (BottomBlock is defined at module scope above to keep React reconciliation stable.)

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Desktop Sidebar (>= lg / 1024px) */}
      <aside className="hidden lg:flex flex-col w-64 xl:w-72 glass-sidebar border-r border-slate-200 fixed inset-y-0 left-0 z-30">
        {/* Logo (fixed height) */}
        <div className="flex-shrink-0 px-5 py-5 border-b border-slate-200">
          <Link to="/dashboard" className="block text-center" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Dolphin Retail Media"
              className="h-10 xl:h-12 w-auto mx-auto"
            />
            <p className="mt-2 text-[10px] font-semibold tracking-[0.22em] text-[#006994]">
              DOLPHIN LEADFLOW
            </p>
          </Link>
        </div>

        {/* Scrollable nav — grows + scrolls if too many items for the viewport */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-1 nav-scroll">
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              active={isActive(item.to)}
            />
          ))}
        </nav>

        {/* Always-visible bottom block */}
        <BottomBlock user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile / Tablet Header (< lg) */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-header border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-logo">
            <img src={LOGO_URL} alt="Dolphin Retail Media" className="h-7 sm:h-8 w-auto" />
            <span className="text-[10px] font-semibold tracking-[0.18em] text-[#006994]">
              DOLPHIN LEADFLOW
            </span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 -mr-2 text-slate-500 hover:text-slate-900"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile / Tablet Menu — slide-down full-height with scrollable nav + sticky bottom */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 top-14 z-30 bg-slate-900/40"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-backdrop"
          />
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed top-14 left-0 bottom-0 z-40 w-72 sm:w-80 max-w-[85vw] flex flex-col glass-sidebar border-r border-slate-200"
            data-testid="mobile-menu"
          >
            <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-1 nav-scroll">
              {navItems.map((item) => (
                <NavItem
                  key={item.to}
                  {...item}
                  active={isActive(item.to)}
                  onClick={() => setMobileMenuOpen(false)}
                />
              ))}
            </nav>
            <BottomBlock user={user} onLogout={handleLogout} compact />
          </motion.aside>
        </>
      )}

      {/* Main Content
          Padding-top accounts for the mobile header (h-14).
          Margin-left matches the desktop sidebar width (64 / 72). */}
      <main className="flex-1 lg:ml-64 xl:ml-72 pt-14 lg:pt-0 min-w-0 overflow-x-hidden">
        <div className="p-4 sm:p-6 lg:p-8 xl:p-10 max-w-full">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
